// Nodera Knowledge Base — config shared by the dashboard, list, and detail pages.
//
// Each entry mirrors the backend KB_DATABASES (kept in sync by hand for now).
// `icon` resolves via lucide-react. `cls` carries pre-baked Tailwind class
// strings so we never use dynamic class concatenation (which the JIT can't
// see at build-time).

import {
  Layers,
  Network,
  Stethoscope,
  Workflow,
  Megaphone,
  Calculator,
  Radar,
} from "lucide-react";

const ACCENT_CLASSES = {
  sky: {
    text: "text-sky-400",
    textSoft: "text-sky-300",
    ring: "ring-sky-500/20",
    hoverBorder: "hover:border-sky-500/40",
  },
  emerald: {
    text: "text-emerald-400",
    textSoft: "text-emerald-300",
    ring: "ring-emerald-500/20",
    hoverBorder: "hover:border-emerald-500/40",
  },
  indigo: {
    text: "text-indigo-400",
    textSoft: "text-indigo-300",
    ring: "ring-indigo-500/20",
    hoverBorder: "hover:border-indigo-500/40",
  },
  violet: {
    text: "text-violet-400",
    textSoft: "text-violet-300",
    ring: "ring-violet-500/20",
    hoverBorder: "hover:border-violet-500/40",
  },
  rose: {
    text: "text-rose-400",
    textSoft: "text-rose-300",
    ring: "ring-rose-500/20",
    hoverBorder: "hover:border-rose-500/40",
  },
  amber: {
    text: "text-amber-400",
    textSoft: "text-amber-300",
    ring: "ring-amber-500/20",
    hoverBorder: "hover:border-amber-500/40",
  },
};

const withClasses = (accent) => ACCENT_CLASSES[accent] || ACCENT_CLASSES.sky;

export const KB_DATABASES = [
  {
    key: "programs",
    label: "Program Intelligence",
    description: "Structured database of cash-pay healthcare programs.",
    icon: Layers,
    primary: "program_name",
    accent: "sky",
    cls: withClasses("sky"),
    ai: ["summarize", "trends", "recommend-vendors"],
  },
  {
    key: "vendors",
    label: "Vendor Intelligence",
    description: "Detailed intelligence on every vendor in (or considered for) the network.",
    icon: Network,
    primary: "vendor_name",
    accent: "emerald",
    cls: withClasses("emerald"),
    ai: ["summarize", "trends"],
  },
  {
    key: "clinics",
    label: "Clinic Intelligence",
    description: "Market feedback and intelligence gathered from clinics.",
    icon: Stethoscope,
    primary: "clinic_name",
    accent: "indigo",
    cls: withClasses("indigo"),
    ai: ["summarize", "trends", "recommend-programs"],
  },
  {
    key: "workflows",
    label: "Workflow Intelligence",
    description: "Reusable workflow infrastructure across programs.",
    icon: Workflow,
    primary: "workflow_name",
    accent: "violet",
    cls: withClasses("violet"),
    ai: ["summarize", "trends"],
  },
  {
    key: "marketing",
    label: "Marketing Intelligence",
    description: "Campaigns and messaging library — performance + creative.",
    icon: Megaphone,
    primary: "campaign_name",
    accent: "rose",
    cls: withClasses("rose"),
    ai: ["summarize", "trends"],
  },
  {
    key: "economics",
    label: "Program Economics",
    description: "Financial viability + unit economics across every program.",
    icon: Calculator,
    primary: "program",
    accent: "amber",
    cls: withClasses("amber"),
    ai: ["summarize", "trends"],
  },
  {
    key: "competitors",
    label: "Competitive Intelligence",
    description: "Competitors, market signals, positioning, and threats.",
    icon: Radar,
    primary: "company",
    accent: "rose",
    cls: withClasses("rose"),
    ai: ["summarize", "trends", "competitive-threats"],
  },
];

export const DB_BY_KEY = Object.fromEntries(KB_DATABASES.map((d) => [d.key, d]));
